import { AuthenticationContext, Policy } from '~/contexts/AuthenticationContext';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { useLogoutMutation } from '~/hooks/useLogoutMutation';
import { useLoginMutation } from '~/hooks/useLoginMutation';
import { useLocalStorage } from '@uidotdev/usehooks';
import { checkUserPolicy } from '~/utils/policy';
import { Nullish } from '~/types/Nullish';
import { User } from '~/apis/ssf';

export const LS_EXPIRY_KEY = '_auth.expires_at';
export const LS_TOKEN_KEY = '_auth.token';
export const LS_USER_KEY = '_auth.user';

export function AuthenticationProvider({ children }: PropsWithChildren) {
  const logoutMutation = useLogoutMutation();
  const loginMutation = useLoginMutation();

  const [expiry, setExpiry] = useLocalStorage<Nullish<string>>(LS_EXPIRY_KEY, undefined);
  const [token, setToken] = useLocalStorage<Nullish<string>>(LS_TOKEN_KEY, undefined);
  const [user, setUser] = useLocalStorage<Nullish<User>>(LS_USER_KEY, undefined);

  const isExpired = useMemo(() => {
    return expiry ? new Date(expiry) < new Date() : true;
  }, [expiry]);

  const isAuthenticated = useMemo(() => {
    return !isExpired && !!token && !!user;
  }, [isExpired, token, user]);

  const isSuperUser = useMemo(() => {
    return user?.permissions.federation_admin ?? false;
  }, [user]);

  useEffect(() => {
    if (isExpired) {
      setExpiry(null);
      setToken(null);
      setUser(null);
    }
  }, [isExpired, setExpiry, setToken, setUser]);

  async function login(username: string, password: string) {
    const { data: response } = await loginMutation.mutateAsync({ username, password });

    setExpiry(response.expiry);
    setToken(response.token);
    setUser(response.user);
  }

  async function logout() {
    await logoutMutation.mutateAsync();

    setExpiry(null);
    setToken(null);
    setUser(null);
  }

  function can(policy: Policy, entityId?: number, checkAnyClubAdmin?: boolean): boolean {
    if (!user) {
      return false;
    }

    return checkUserPolicy(user.permissions, policy, entityId, checkAnyClubAdmin);
  }

  return (
    <AuthenticationContext.Provider value={{ setUser, login, logout, can, token, user, isAuthenticated, isSuperUser }}>
      {children}
    </AuthenticationContext.Provider>
  );
}
